import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ClearIcon from '@mui/icons-material/Clear';
import type { DocumentQuestion, DocumentQuestionAnswer } from '../../../core/types/data-models';

/**
 * Clarifying questions the AI prepared before generating a document — each
 * with an optional suggested answer; unanswered ones are skipped.
 */
export function DocumentQuestionsDialog({
  open,
  docTypeName,
  questions,
  submitting,
  error,
  onClose,
  onSubmit,
}: {
  open: boolean;
  docTypeName: string;
  questions: DocumentQuestion[];
  submitting: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (answers: DocumentQuestionAnswer[]) => void;
}) {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  // Question id whose suggestion is being previewed (hovered/focused "Use
  // suggested answer" button). Shown in the field without committing it.
  const [previewId, setPreviewId] = useState<string | null>(null);

  // Reset the answers whenever a new set of questions opens the dialog. Kept
  // stable across close/reopen of the same set, so typed answers survive when
  // the user dismisses the dialog and reopens it from the banner.
  useEffect(() => {
    setAnswers({});
  }, [questions]);

  // Only questions the user actually answered become Q&A pairs; skipped ones are omitted.
  const answeredPairs = (): DocumentQuestionAnswer[] =>
    questions
      .map((q) => ({ question: q.question, answer: (answers[q.id] ?? '').trim() }))
      .filter((qa) => qa.answer.length > 0);

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Questions for {docTypeName}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Answering these helps the generated document match what you need. You can skip
          any question.
        </Typography>
        <Stack spacing={2.5}>
          {questions.map((q) => {
            const currentAnswer = answers[q.id] ?? '';
            const isPreviewing = previewId === q.id && !!q.suggestedAnswer;
            const isSuggestionApplied = !!q.suggestedAnswer && currentAnswer === q.suggestedAnswer;
            const stopPreview = () =>
              setPreviewId((cur) => (cur === q.id ? null : cur));
            return (
              <Box key={q.id}>
                <TextField
                  label={q.question}
                  helperText={q.why}
                  placeholder={q.suggestedAnswer}
                  value={isPreviewing ? q.suggestedAnswer : currentAnswer}
                  onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
                  disabled={submitting}
                  InputProps={{ readOnly: isPreviewing }}
                  multiline
                  minRows={2}
                  fullWidth
                />
                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                  {q.suggestedAnswer && (
                    <Button
                      size="small"
                      startIcon={<AutoAwesomeIcon fontSize="small" />}
                      onClick={() =>
                        setAnswers((prev) => ({ ...prev, [q.id]: q.suggestedAnswer! }))
                      }
                      onMouseEnter={() => setPreviewId(q.id)}
                      onMouseLeave={stopPreview}
                      onFocus={() => setPreviewId(q.id)}
                      onBlur={stopPreview}
                      disabled={submitting || isSuggestionApplied}
                    >
                      {isSuggestionApplied ? 'Suggestion applied' : 'Use suggested answer'}
                    </Button>
                  )}
                  {currentAnswer && (
                    <Button
                      size="small"
                      color="inherit"
                      startIcon={<ClearIcon fontSize="small" />}
                      onClick={() => setAnswers((prev) => ({ ...prev, [q.id]: '' }))}
                      disabled={submitting}
                    >
                      Clear
                    </Button>
                  )}
                </Stack>
              </Box>
            );
          })}
        </Stack>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button onClick={onClose} color="inherit" disabled={submitting}>
          Cancel
        </Button>
        <Stack direction="row" spacing={1}>
          <Button onClick={() => onSubmit([])} color="inherit" disabled={submitting}>
            Skip All & Generate
          </Button>
          <Button
            variant="contained"
            startIcon={<AutoAwesomeIcon />}
            onClick={() => onSubmit(answeredPairs())}
            disabled={submitting}
          >
            {submitting ? 'Starting…' : 'Generate'}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
